import { 
  store, 
  getContext, 
  getElement,
} from '@wordpress/interactivity';
import {gsap} from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const slideClassName = `wp-block-cover`;
const activeClassName = `${slideClassName}--active`;
const pinSpacerClassName = 'pin-spacer';

const normalSlideYStartPosition = `110vh`;
const titleSlideDuration = 1;
const normalSlideDuration = 1;

const { state, actions } = store("amplify_storytelling_floatMotionBackgroundSwap", {
  state: { },

  actions: { },

  callbacks: {
    init: () => {
      console.log(`[floatMotionBackgroundSwap.init] Loading...`)
      const context = getContext();
      console.log(`[floatMotionBackgroundSwap.init] context:`, context)
      const { ref } = getElement();
      // console.log(`[floatMotionBackgroundSwap.init] Root element:`, ref)

      const isTitleSlideEnabled = context.isFirstSlideTitleSlide;
      // console.log(`[floatMotionBackgroundSwap.init] isTitleSlideEnabled:`, isTitleSlideEnabled)

      const allSlides = Array.from(ref.querySelectorAll(`.${slideClassName}`));
      // console.log(`[floatMotionBackgroundSwap.init] allSlides:`, allSlides)

      const innerContainers = allSlides
        .map((coverSlide) => {
          return coverSlide.querySelector(".wp-block-cover__inner-container");
        })
        .filter((innerContainer) => innerContainer !== null);
      // console.log(`[floatMotionBackgroundSwap.init] innerContainers:`, innerContainers)

      // If no slides are found, quit initialization early.
      if (allSlides.length === 0) {
        console.error(`[floatMotionBackgroundSwap.init] No slides found.`);
        return;
      }
      // If the slides don't match the containers count, quit initialization early.
      if (innerContainers.length !== allSlides.length) {
        console.error(`[floatMotionBackgroundSwap.init] Slide composition error. Found ${allSlides.length} slides and ${innerContainers.length} inner containers.`);
        return;
      }

      // Add the active class to the first slide.
      const firstSlide = allSlides[0];
      firstSlide.classList.add(activeClassName);

      // Change the (top) position of the inner containers.
      innerContainers.forEach((slide, index) => {
        // Don't adjust the first slide if we have title slide behavior enabled.
        if (index === 0 && isTitleSlideEnabled) {
          return;
        }

        slide.style.top = normalSlideYStartPosition;
      });

      // Calculate the total animation length.
      const animationLength = isTitleSlideEnabled
        ? (allSlides.length - 0.5) * 100
        : allSlides.length * 100;


      const setActiveSlide = (index) => {
        // Massage index to ensure it's within bounds.
        index = Math.max(0, Math.min(index, allSlides.length - 1));

        allSlides.forEach((img, idx) => {
          if (idx === index && !img.classList.contains(activeClassName)) {
            img.classList.add(activeClassName);
          } else if (idx !== index && img.classList.contains(activeClassName)) {
            img.classList.remove(activeClassName);
          }
        });
      };

      // Set up the container pinning, as well as the active slide swap routine.
      let tl = gsap.timeline({
        scrollTrigger: {
          trigger: ref,
          start: "top top",
          end: `+=${animationLength}%`,
          scrub: true,
          pin: true,
          pinSpacing: true, // Need this to simulate slide length.
          onUpdate: () => {
            console.log(`[floatMotionBackgroundSwap tl] onUpdate, ref:`, ref);
          },
          onRefresh: () => {
            console.log(`[floatMotionBackgroundSwap onRefresh] ref:`, ref);
            const parentNode = ref.parentNode;

            if (parentNode) {
              console.log(`[floatMotionBackgroundSwap onRefresh] parentNode:`, parentNode);
              const hasAlignFull = parentNode.classList.contains('alignfull');
              console.log(`[floatMotionBackgroundSwap onRefresh] hasAlignFull:`, hasAlignFull);

              const {marginLeft, marginRight} = window.getComputedStyle(parentNode);
              console.log(`[floatMotionBackgroundSwap onRefresh] marginLeft:`, marginLeft, `marginRight:`, marginRight);
            }
          },
        }
      });

      /*
        ScrollTrigger pinning introduces a new div.pin-spacer parent around the 
        pinned element (in this case, the element pointed to by `ref`).
        This pin-spacer element lacks the Gutenberg layout classes that have 
        been added to the block element, which in turn causes the block to have
        spacing and alignment issues.
        To fix this, we'll hoist any of the Gutenberg layout classes from the 
        block element to the pin-spacer element.
      */
      const layoutClasses = [
        'alignfull',
        'alignwide',
        'alignleft',
        'alignright',
        'is-layout-flow',
        // TODO: there may be others to add...
      ];
      const pinSpacer = ref.closest(`.${pinSpacerClassName}`);

      // If the pin-spacer element can't be found, something went wrong, so
      // let's log an error and quit early.
      if (!pinSpacer) {
        console.error(`[floatMotionBackgroundSwap.init] pin-spacer element not found. Aborting initialization.`);
        return;
      }

      layoutClasses.forEach((layoutClass) => {
        if (ref.classList.contains(layoutClass)) {
          pinSpacer.classList.add(layoutClass);
        }
      });

      // Set up the slide inner content animations.
      innerContainers.forEach((slide, index) => {
        const isTitleSlide = isTitleSlideEnabled && index === 0;

        tl.fromTo(
          slide,
          {
            y: () => 0,
            ease: 'none',
          },
          {
            y: () => {
              return isTitleSlide 
                ? -(window.innerHeight + slide.offsetHeight) 
                : -(window.innerHeight + slide.offsetHeight * 2);
            },
            duration: isTitleSlide ? titleSlideDuration : normalSlideDuration,
            ease: 'none',
            // Full events list here: https://gsap.com/cheatsheet/
            onStart: () => {
              console.log(`[floatMotionBackgroundSwap tl] slide #${index} onStart`);

              setActiveSlide(index);
            },
            onReverseComplete: () => {
              console.log(`[floatMotionBackgroundSwap tl] slide #${index} onReverseComplete`);

              setActiveSlide(index - 1);
            },
          },
        );
      });
    },
  },
});
